import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useMutation } from "convex/react";
import { toast } from "sonner";
import { api } from "../../convex/_generated/api";
import { useStable } from "../lib/useStable";
import { cn } from "@/lib/utils";
import { Card as UiCard } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loading, Empty, ScreenBoundary } from "../components/states";
import {
  ActionBar,
  Button,
  Chips,
  CollapsibleSection,
  ModelledTag,
  Money,
  Pill,
  QtyStepper,
  SectionHeading,
  Tile,
  plural,
  shortDate,
  relativeDay,
} from "../components/kit";
import { useBusyAction } from "../lib/useBusyAction";
import {
  formatCentsCompact,
  parseDollarsToCents,
  plainAmount,
  sanitiseDollarInput,
} from "@shared/money";
import { TONE_EDGE } from "@shared/status";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import { useHeaderOffset } from "../lib/useHeaderOffset";

/**
 * The warehouse shelf — Gloria's front door.
 *
 * `homeFor("warehouse")` lands here, so this is the first thing the heaviest
 * daily user sees. Three jobs, in the order she does them standing in the
 * stockroom holding a box:
 *
 *   1. **Book a vendor delivery in.** One tap from the list to a quantity
 *      panel, one tap to confirm. This is the half of her workflow that had no
 *      completion at all before — nothing in the app could add warehouse stock.
 *   2. **Answer "do I need to reorder?"** — on-hand against minimum, coloured
 *      red / green / gray exactly as the sheet she reads today does: red needs
 *      ordering, green already ordered from the vendor, gray fine.
 *   3. **Manage the shelf** — bin location, minimum level, expiry date. All
 *      three were unwritable until now, which is why they are empty on all 97
 *      items.
 *
 * 390px is the primary viewport. Single column, 44px minimum tap targets,
 * nothing that depends on hover, no horizontal table.
 *
 * **Ninety-seven rows is a wall unless it is divided, and dividing it is not
 * enough on its own.** The list is broken into the same sections the client's
 * own order sheet uses (`subcategory` — GLOVES, ENDO, STERILIZATION…), and each
 * one *folds*: a closed section unmounts its rows, so the screen she lands on is
 * a dozen headers and a working stretch of shelf rather than ninety-seven
 * cards. Every header says its count and how many of its items need ordering
 * before it is opened, which is what makes the list scannable at 390px.
 *
 * The server's "worst first" sort is preserved *inside* every section, and the
 * sections themselves come out in the order their worst item appeared — so the
 * section carrying the most urgent item is still the first thing on screen, and
 * the top of the list arrives open (see `ROWS_OPEN_ON_ARRIVAL`).
 *
 * **Cost, never margin.** `nexoraCost` is on this screen on purpose — SPEC.md,
 * "What exactly is owner-only": the warehouse cannot value a shelf without it.
 * `listPrice` alongside it, the spread, the savings share and anything derived
 * from them are owner-only and must never be added here.
 *
 * **Not shown: "available".** `stock.reserved` is 0 everywhere because nothing
 * writes it, so an availability figure would be on-hand under a label that
 * claims to know about commitments it does not. See convex/inventory.ts.
 */

/* -------------------------------------------------------------------------- */
/* Small helpers                                                               */
/* -------------------------------------------------------------------------- */

type Row = NonNullable<
  ReturnType<typeof useQuery<typeof api.inventory.list>>
>["rows"][number];

/** `<input type="date">` speaks YYYY-MM-DD. Anchored at midday UTC so a
 *  timezone offset can never move a printed expiry date by a day. */
const toDateInput = (ms: number | null): string =>
  ms === null ? "" : new Date(ms).toISOString().slice(0, 10);

const fromDateInput = (value: string): number | null => {
  if (!value) return null;
  const ms = Date.parse(`${value}T12:00:00.000Z`);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * The row grid, shared by the column headings and every row.
 *
 * Below `lg` this is a wrapping flex row — name and count on the first line,
 * status and the action on the second. At `lg` the same four blocks stop
 * wrapping and line up as columns, which is what turns a hundred stacked cards
 * into something a person can scan down. One string, so a heading and its
 * column cannot drift apart.
 */
const COL_NAME = "order-1 min-w-0 flex-1";
const COL_COUNT = "order-2 shrink-0 text-right lg:w-16";
/** Display is applied by the caller: most rows have nothing to say here, and a
 *  status block that is empty must not claim a line on a phone. */
const COL_STATUS =
  "order-4 min-w-0 basis-full flex-wrap items-center gap-1.5 lg:order-3 lg:w-48 lg:flex-none lg:basis-auto";
const COL_ACTION = "order-3 shrink-0 lg:order-4 lg:w-28";

/**
 * Red = needs ordering, green = ordered, and *nothing at all* = fine.
 *
 * The same colours as the spreadsheet, applied to the same question. An item
 * under its minimum that a manager already ordered from the vendor is green,
 * not red — that is the whole reason `items.lastOrderedAt` is on this payload.
 *
 * The third case used to be a gray "In stock" pill, and on this catalogue that
 * meant printing it ninety-one times. A label that appears on nearly every row
 * carries no information and costs every row a line; the shelf being fine is
 * the default, so it is said by silence. The six rows that are not fine are
 * what the eye should find, and they now do.
 */
function reorderPill(row: Row): ReactNode {
  if (row.reorderState === "needs-ordering") {
    return (
      <Pill
        tone="red"
        label="Needs ordering"
        qualifier={row.outOfStock ? "none left" : `${row.shortfall} short`}
      />
    );
  }
  if (row.reorderState === "on-order") {
    return (
      <Pill
        tone="green"
        label="Ordered"
        qualifier={row.lastOrderedAt ? shortDate(row.lastOrderedAt) : null}
      />
    );
  }
  return null;
}

/**
 * Expiry is a different axis from order status, and only two of its states are
 * worth saying out loud.
 *
 * **Expired is red** — a box past its date is stock that must come off the
 * shelf, which is the same "somebody must act" red as an item that needs
 * ordering, and it is a status.
 *
 * **Expiring soon is not.** It used to be an amber pill, and amber now means
 * urgency (`UrgencyTag`) and modelled figures (`ModelledTag`) app-wide — a third
 * meaning on the same hue on the screen with the most rows in the app is how a
 * colour stops meaning anything. It is a *qualifier* on a row that is otherwise
 * fine, so it gets the treatment the design system already prescribes for
 * qualifiers: muted grey text beside the pills, no chip, no colour. The date is
 * the information; the colour was never carrying any of it.
 *
 * A function rather than a component, like `reorderPill`, so the row can ask
 * whether either of them actually produced a node before it gives the status
 * column a line. That question used to be answered by a second copy of these
 * conditions, and the copy disagreed: it counted `expiryState === "soon"` on a
 * row with no `expiresAt` at all — which this bails out on — so the column
 * claimed a line on a phone to render nothing.
 */
function expiryNote(row: Row): ReactNode {
  if (row.expiresAt === null) return null;
  if (row.expiryState === "expired") {
    return <Pill tone="red" label="Expired" qualifier={shortDate(row.expiresAt)} />;
  }
  if (row.expiryState === "soon") {
    return (
      <span className="text-xs font-medium whitespace-nowrap text-muted-foreground">
        Expires {shortDate(row.expiresAt)}
      </span>
    );
  }
  return null;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm">{children}</dd>
    </div>
  );
}

/* No field constants here any more. The two inline panels below use the
   registry's `<Input>` and `<Label>` exactly as shipped: the base is already
   `text-base md:text-sm` (so a phone keyboard does not zoom), the edge is
   `border-input`, and the focus ring is `--ring`. What used to live here was a
   ring-and-height ladder undoing all three, and since the foundation moved it
   was drawing the registry border *and* its own ring on top. */

/* -------------------------------------------------------------------------- */
/* Book a delivery in                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The whole packing slip, in one submission.
 *
 * Parked in the shared `ActionBar` while anything is in the delivery, so she can
 * keep scrolling and tapping items without losing the running total. One slip
 * number for the lot — she types it once, off the paper in her hand, rather
 * than once per line.
 *
 * Where the bar sits and how it lines up with the content column is `ActionBar`'s
 * business, not this screen's; this used to restate all of it and had drifted
 * from the other copy by a missing `pb-safe`.
 */
function DeliveryBar({
  rows,
  delivery,
  onQty,
  onClear,
}: {
  rows: Row[];
  delivery: Map<Row["itemId"], number>;
  onQty: (itemId: Row["itemId"], qty: number) => void;
  /** Empty the delivery. Booking it in and abandoning it end the same way, and
   *  the screen passed the same handler to both. */
  onClear: () => void;
}) {
  const receive = useMutation(api.inventory.receiveFromVendor);
  const [reference, setReference] = useState("");
  const [expanded, setExpanded] = useState(false);
  const { busy, run } = useBusyAction();

  const lines = [...delivery.entries()];
  const units = lines.reduce((total, [, qty]) => total + qty, 0);

  const submit = () => {
    if (lines.length === 0) return;
    return run(
      async () => {
        const result = await receive({
          lines: lines.map(([itemId, qty]) => ({ itemId, qty })),
          reference: reference.trim() || null,
        });
        // Success only: a refusal must leave the slip number and the delivery
        // exactly where she left them.
        setReference("");
        onClear();
        return result;
      },
      {
        failed: "Could not book that delivery in",
        done: (result) => {
          const stillShort = result.lines.filter(
            (line) => line.belowMinimum,
          ).length;
          /* "Booked in 6 lines · 12 units" was the mutation's return value read
             aloud. She unpacked *items*, not lines, and the second half now says
             what it means for the shelf. The words "Booked in" are load-bearing
             — `tools/clickcount.mjs` waits on them. */
          const happened =
            `Booked in ${result.lineCount} ${plural(result.lineCount, "item")} · ` +
            `${result.unitsIn} ${plural(result.unitsIn, "unit")} on the shelf`;

          if (stillShort > 0) {
            return {
              happened,
              next:
                `${stillShort} of them ${stillShort === 1 ? "is" : "are"} still under ` +
                "the minimum, so they stay on the reorder list.",
              check: true,
            };
          }
          return {
            happened,
            next:
              result.resolvedAlerts > 0
                ? `Everything is back above minimum — ${result.resolvedAlerts} low-stock ` +
                  `${plural(result.resolvedAlerts, "alert")} cleared.`
                : "Everything on the slip is at or above its minimum.",
          };
        },
      },
    );
  };

  if (lines.length === 0) return null;

  return (
    <ActionBar className="space-y-2">
      {/* Stock `ghost`. Two classes survive on it and both are the 390px
          case: `whitespace-normal` because the base's `nowrap` inherits into
          both spans and a three-digit count would push "Check quantities" out
          of a fixed bar that has nowhere to overflow to, and `h-auto` so the
          row can be two lines tall when it does wrap. */}
      <Button
        variant="ghost"
        onClick={() => setExpanded((open) => !open)}
        className="h-auto w-full justify-between gap-3 py-2 text-left whitespace-normal"
      >
        <span className="text-sm font-semibold">
          Delivery · {lines.length} {plural(lines.length, "item")} · {units}{" "}
          {plural(units, "unit")}
        </span>
        <span className="text-xs font-medium text-primary underline">
          {expanded ? "Hide" : "Check quantities"}
        </span>
      </Button>

      {expanded ? (
        <div className="max-h-52 space-y-1.5 overflow-y-auto">
          {lines.map(([itemId, qty]) => {
            // Only rows currently on screen can be shown with a name; the map
            // is the source of truth for what gets sent. A dozen lines against
            // a hundred rows is a scan worth doing here, and only while the
            // list is open — the index this used to build was rebuilt on every
            // keystroke of the search box above, open or not.
            const name = rows.find((row) => row.itemId === itemId)?.name;
            return (
              <div key={itemId} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm">
                  {name ?? "Item"}
                </span>
                <QtyStepper
                  value={qty}
                  onChange={(next) => onQty(itemId, next)}
                  label={name ?? "item"}
                />
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        {/* `aria-label` is load-bearing, not decoration: `tools/clickcount.mjs`
            fills this field by it on the six-line packing-slip run. Stock
            `<Input>` otherwise — only the row's own widths are set here. */}
        <Input
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          placeholder="Packing slip #"
          aria-label="Packing slip or vendor invoice number"
          className="min-w-0 flex-1 lg:max-w-xs"
        />
        {/* `lg`: this is the delivery's primary action and it is pressed with a
            box under one arm. */}
        <Button size="lg" onClick={submit} disabled={busy}>
          {busy ? "Booking in…" : `Book in ${lines.length}`}
        </Button>
        {/* Matched to the primary beside it — a 36px button next to a 40px one
            in the same row reads as a mistake, not as a hierarchy. */}
        <Button size="lg" variant="ghost" onClick={onClear} disabled={busy}>
          Clear
        </Button>
      </div>
    </ActionBar>
  );
}


/**
 * The stock-in panel.
 *
 * Quantity is pre-filled with whatever gets the item back to its minimum, so
 * the common case — "the thing I was short of turned up" — is a single confirm
 * tap. Stepper buttons are 44px because this gets used with a box under one arm
 * and possibly gloves on.
 */
function BookIn({
  row,
  onClose,
}: {
  row: Row;
  /** Booked in and cancelled both close the panel, and the row passed the same
   *  handler to both. One prop, so they cannot be wired up differently. */
  onClose: () => void;
}) {
  const receive = useMutation(api.inventory.receiveFromVendor);
  const [qty, setQty] = useState(Math.max(1, row.shortfall));
  const [expiry, setExpiry] = useState(toDateInput(row.expiresAt));
  const [reference, setReference] = useState("");
  const { busy, run } = useBusyAction();
  /* One id per panel instance. Several of these panels can be mounted at once
     — one per open row — so a hard-coded id would point two labels at the same
     field. */
  const fieldId = useId();

  const submit = () =>
    run(
      async () => {
        const result = await receive({
          lines: [
            {
              itemId: row.itemId,
              qty,
              // Only send an expiry when it changed — an untouched field must
              // not rewrite the batch date that is already on the shelf.
              ...(expiry !== toDateInput(row.expiresAt)
                ? { expiresAt: fromDateInput(expiry) }
                : {}),
            },
          ],
          reference: reference || null,
        });
        // Success only: a refusal leaves the panel open on what she typed.
        onClose();
        return result;
      },
      {
        failed: "Could not book that in",
        done: (result) => {
          const line = result.lines[0];
          const happened = `Booked in ${result.unitsIn} × ${row.name}`;
          if (line.belowMinimum) {
            return {
              happened,
              next:
                `${line.onHand} on hand — still under the minimum of ${line.minStockLevel}, ` +
                "so it stays on the reorder list.",
              check: true,
            };
          }
          return {
            happened,
            next:
              result.resolvedAlerts > 0
                ? `${line.onHand} on hand — back above minimum, low-stock alert cleared.`
                : `${line.onHand} on hand.`,
          };
        },
      },
    );

  return (
    <div className="mt-3 space-y-3 rounded-lg border bg-muted/50 p-3">
      <div className="flex items-center gap-2 lg:max-w-sm">
        <QtyStepper
          size="lg"
          value={qty}
          onChange={setQty}
          label={`Quantity received of ${row.name}`}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:max-w-lg">
        <div className="grid min-w-0 gap-2">
          <Label htmlFor={`${fieldId}-expiry`}>Expiry (optional)</Label>
          <Input
            id={`${fieldId}-expiry`}
            type="date"
            value={expiry}
            onChange={(event) => setExpiry(event.target.value)}
            aria-label="Expiry date on the box"
          />
        </div>
        <div className="grid min-w-0 gap-2">
          <Label htmlFor={`${fieldId}-slip`}>Packing slip</Label>
          <Input
            id={`${fieldId}-slip`}
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            placeholder="HS invoice #"
            aria-label="Packing slip or vendor invoice number"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          size="lg"
          className="flex-1 lg:flex-none lg:px-8"
          disabled={busy}
          onClick={submit}
        >
          {busy ? "Booking in…" : `Book in ${qty}`}
        </Button>
        {/* Matched to the primary beside it. */}
        <Button size="lg" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Edit the shelf details                                                      */
/* -------------------------------------------------------------------------- */

/**
 * What the figure in the cost field is worth, said beside it.
 *
 * `ModelledTag` is the app-wide affordance for "this number is ours, not
 * theirs" — the same amber chip the owner's pricing and invoice screens wear —
 * and it belongs here for the same reason it belongs there: the caveat travels
 * with the number, on the same line, wherever the number is shown. The other
 * three bases are plain grey text; only a modelled figure is a claim that needs
 * qualifying.
 */
function CostBasisNote({ basis }: { basis: string }) {
  if (basis === "modelled") return <ModelledTag />;
  const said =
    basis === "entered"
      ? "Entered"
      : basis === "sheet"
        ? "From the sheet"
        : "No cost on file";
  return <span className="text-xs text-muted-foreground">{said}</span>;
}

function EditItem({
  row,
  onClose,
}: {
  row: Row;
  /** Saved and cancelled both close the panel. One prop, as above. */
  onClose: () => void;
}) {
  const update = useMutation(api.inventory.updateItem);
  const setCost = useMutation(api.inventory.setCost);
  const [bin, setBin] = useState(row.binLocation);
  const [min, setMin] = useState(String(row.minStockLevel));
  const [expiry, setExpiry] = useState(toDateInput(row.expiresAt));
  /* Dollars and cents as text, not a number: `1189` cents is `"11.89"` to the
     person holding the phone, and the round trip back to cents happens once, in
     `parseDollarsToCents`, with no float in the middle. `plainAmount` is the
     house form of a bare amount — `formatCents` would seed the field with
     "$1,189.00", which is a thing to read rather than a thing to edit. */
  const costAsGiven =
    row.nexoraCostCents === null ? "" : plainAmount(row.nexoraCostCents);
  const [cost, setCostText] = useState(costAsGiven);
  /**
   * Whether the cost field was touched at all — not whether its text differs.
   *
   * Retyping the modelled figure digit for digit is the gesture that says "I
   * have checked this against the invoice and it is right", and it has to be
   * sendable: the server flips the basis from a guess to a confirmed number on
   * the strength of it. Comparing strings would drop exactly that save. An
   * untouched field sends nothing, so saving a bin edit can never quietly
   * confirm a cost nobody looked at.
   */
  const [costEdited, setCostEdited] = useState(false);
  const { busy, run } = useBusyAction();
  /** As in `BookIn`: one id per mounted panel, not one per screen. */
  const fieldId = useId();

  /* Blank means "leave it alone", the same convention `updateItem` uses for an
     absent argument. There is no way to *clear* a cost: zero is not a cost, and
     "no cost on file" is `pricePending`, which is not this field's business. */
  const costTouched = costEdited && cost.trim() !== "";

  const submit = () =>
    run(
      async () => {
        /* Refused before anything is sent, so a mistyped cost cannot land a
           half-saved panel. The message is the one the toast reads out. */
        const cents = costTouched ? parseDollarsToCents(cost) : null;
        if (costTouched && cents === null) {
          throw new Error(
            "Enter the actual cost as dollars and cents — 11.89, not 11.891",
          );
        }

        const result = await update({
          itemId: row.itemId,
          binLocation: bin,
          minStockLevel: Number(min || 0),
          expiresAt: fromDateInput(expiry),
        });
        /* Its own mutation and so its own transaction, deliberately: the cost
           carries a different secret and its own gate, and folding it into
           `updateItem` would have widened what that mutation is allowed to
           write. Second, so a shelf edit is not held up by a cost the server
           refuses. */
        const costResult =
          cents === null
            ? null
            : await setCost({ itemId: row.itemId, nexoraCostCents: cents });

        const changed = [...result.changed, ...(costResult?.changed ?? [])];
        /* "Nothing changed" is a bare `toast` — neither a done nor a check —
           and `Outcome` has no level for "that was a no-op", so it stays here
           rather than being promoted to a confirmation the save did not earn.
           `done` returns null for the same case, so only one toast is spoken. */
        if (changed.length === 0) toast("Nothing changed");
        onClose();
        return { ...result, changed, costResult };
      },
      {
        failed: "Could not save the shelf details",
        done: (result) => {
          if (result.changed.length === 0) return null;
          /* Two things can want the reader's attention, and only one sentence
             is going to be read: a shelf that has just dropped under its
             minimum, or a cost that leaves nothing to pass on. */
          const caution = result.lowStockAlertRaised
            ? " — it is now under its minimum, so it has joined the reorder list."
            : result.costResult?.atOrAboveListPrice
              ? " — that cost is at or above what this item lists at, so check it " +
                "against the vendor invoice."
              : "";
          return {
            happened: `${row.name} updated`,
            next: `${result.changed.join(" · ")}${caution}`,
            check:
              result.lowStockAlertRaised ||
              (result.costResult?.atOrAboveListPrice ?? false),
          };
        },
      },
    );

  return (
    <div className="mt-3 space-y-3 rounded-lg border bg-muted/50 p-3">
      <div className="grid grid-cols-2 gap-3 lg:max-w-lg lg:grid-cols-3">
        <div className="grid min-w-0 gap-2">
          <Label htmlFor={`${fieldId}-bin`}>Bin / location</Label>
          <Input
            id={`${fieldId}-bin`}
            value={bin}
            onChange={(event) => setBin(event.target.value)}
            placeholder="A3"
            aria-label={`Bin location for ${row.name}`}
          />
        </div>
        <div className="grid min-w-0 gap-2">
          <Label htmlFor={`${fieldId}-min`}>Minimum level</Label>
          <Input
            id={`${fieldId}-min`}
            value={min}
            onChange={(event) => setMin(event.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            aria-label={`Minimum stock level for ${row.name}`}
            className="tabular-nums"
          />
        </div>
        <div className="col-span-2 grid min-w-0 gap-2 lg:col-span-1">
          <Label htmlFor={`${fieldId}-expiry`}>
            Expiry date {row.expiryTracked ? "" : "(if applicable)"}
          </Label>
          <Input
            id={`${fieldId}-expiry`}
            type="date"
            value={expiry}
            onChange={(event) => setExpiry(event.target.value)}
            aria-label={`Expiry date for ${row.name}`}
          />
        </div>
        {/* The real acquisition cost. Full width at 390px — a money field
            sharing a line with another field is where a thumb mistypes it.

            **Cost, never the spread.** This panel is the warehouse's, and
            `nexoraCost` is theirs to see and now to set; `listPriceCents`, the
            margin and the savings share are owner-only and must never be put
            beside it here — cost next to list *is* the margin, by subtraction.
            The one thing the server says about the two is a single boolean, and
            it is spoken in a toast rather than printed next to the field. */}
        <div className="col-span-2 grid min-w-0 gap-2 lg:col-span-1">
          <div className="flex flex-wrap items-center gap-2">
            <Label htmlFor={`${fieldId}-cost`}>Actual cost</Label>
            <CostBasisNote basis={row.costBasis} />
          </div>
          <Input
            id={`${fieldId}-cost`}
            value={cost}
            onChange={(event) => {
              setCostText(sanitiseDollarInput(event.target.value));
              setCostEdited(true);
            }}
            /* `decimal`, not `numeric`: the phone keypad has to carry a
               decimal point, and `type="number"` is not an option — see
               `sanitiseDollarInput`. The registry's base is `text-base
               md:text-sm`, so this stays at 16px on the phone and iOS does not
               zoom the panel when it is focused (Request.tsx:617). */
            inputMode="decimal"
            placeholder="11.89"
            aria-label={`Actual cost per unit of ${row.name}, in dollars`}
            className="tabular-nums"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          size="lg"
          className="flex-1 lg:flex-none lg:px-8"
          disabled={busy}
          onClick={submit}
        >
          {busy ? "Saving…" : "Save"}
        </Button>
        {/* Matched to the primary beside it. */}
        <Button size="lg" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* One shelf row                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The left rule, and only the left rule, carries urgency down the list.
 *
 * A hundred rows cannot each be tinted — that is how the old screen read as one
 * undifferentiated wall. A 4px edge is legible in peripheral vision at arm's
 * length and costs the row nothing, and it is the same device the dashboard
 * tiles and the error card already use. Red only where somebody has to do
 * something: below its minimum and not yet ordered, or already expired.
 *
 * The classes come from `TONE_EDGE`, the same ramp the pills and the dashboard
 * tiles read, so "on order" cannot be one green here and another one there.
 */
function edgeFor(row: Row): string {
  if (row.reorderState === "needs-ordering" || row.expiryState === "expired") {
    return TONE_EDGE.red;
  }
  // "Expires soon" deliberately draws no edge: see `expiryNote`. Amber on this
  // list is urgency's colour now, and a shelf edge is not the place to say a
  // date is approaching.
  if (row.reorderState === "on-order") return TONE_EDGE.green;
  return TONE_EDGE.gray;
}

/**
 * One row, memoised.
 *
 * Ninety-seven of these are on screen, and every one of them was being
 * reconciled whenever anything at all changed — a row opening, a section
 * folding, a settled search, the action bar re-measuring itself. `memo` only
 * pays off if the props hold still between renders, which is why the handlers
 * take the row (or its id) as an argument rather than closing over it: the
 * screen can then hand every row the *same* three `useCallback` identities, and
 * the ninety-six rows that did not change re-render not at all.
 */
const ItemCard = memo(function ItemCard({
  row,
  mode,
  onOpen,
  onClose,
  inDelivery,
  onToggleDelivery,
}: {
  row: Row;
  mode: "closed" | "detail" | "in" | "edit";
  onOpen: (itemId: Row["itemId"], mode: "detail" | "in" | "edit") => void;
  onClose: () => void;
  inDelivery: boolean;
  onToggleDelivery: (row: Row) => void;
}) {
  const open = mode !== "closed";
  const short = row.reorderState === "needs-ordering";
  /* The status column's visibility is the pills themselves, not a second
     reading of the same conditions. Ask them what they drew; if both declined,
     there is nothing to give a line to. */
  const reorder = reorderPill(row);
  const expiry = expiryNote(row);
  const hasStatus = reorder !== null || expiry !== null;

  return (
    /* Stock `<Card>`: the edge, the radius and the shadow are the registry's.
       Three classes are set on top and each is this screen's own — `block`
       because the row lays itself out, the tone rule down the left, and the
       `--ring` outline that says which card is open. */
    <UiCard
      data-item={row.itemId}
      className={cn(
        "block gap-0 border-l-4 px-3 py-2.5 transition-shadow lg:px-4 lg:py-2",
        edgeFor(row),
        open ? "ring-2 ring-ring" : "hover:shadow-md",
      )}
    >
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2 lg:flex-nowrap lg:items-center">
        {/* Name. The one thing she is looking for, so it is the only 15px
            thing on the row and the only one at font-semibold.

            Two lines on a phone, because this catalogue's names differ at the
            end — "…Nitrile Glove Medium" against "…Nitrile Glove Large" — and
            truncating to one line cut exactly the word that tells them apart.
            One line at `lg`, where the column is wide enough not to need it.

            Still a raw <button>, deliberately, and it is the clamp above that
            decides it. Three classes in shadcn's button base would each land on
            this one silently: `inline-flex` puts the name and the meta line
            side by side instead of stacked, `whitespace-nowrap` inherits down
            and collapses `line-clamp-2` to the single truncated line this
            comment exists to argue against, and `font-medium` reaches the 11px
            sub-line, which sets no weight of its own. Beating all three back
            would leave the same markup wearing a `data-slot`, so the primitive
            buys nothing here and risks the row that ninety-seven items are read
            from. */}
        <button
          type="button"
          onClick={() => (open ? onClose() : onOpen(row.itemId, "detail"))}
          aria-expanded={open}
          className={cn(COL_NAME, "rounded-md text-left")}
        >
          <div className="line-clamp-2 text-sm leading-tight font-semibold lg:line-clamp-1">
            {row.name}
          </div>
          {/* Bin only when there is one. Printing "no bin" on all ninety-seven
              rows advertised an empty field ninety-seven times without making
              it any easier to fill in. */}
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {row.manufacturer} · {row.packSize || row.subcategory}
            {row.binLocation ? ` · bin ${row.binLocation}` : ""}
          </div>
        </button>

        <div className={COL_COUNT}>
          <div
            className={cn(
              "text-xl leading-none font-bold tabular-nums",
              short && "text-red-700",
            )}
          >
            {row.onHand}
          </div>
          <div className="mt-0.5 text-xs whitespace-nowrap text-muted-foreground tabular-nums">
            min {row.minStockLevel}
          </div>
        </div>

        {/* Empty on nine rows in ten, and on a phone an empty block would
            still take a line. Kept in the flow at `lg` so the column under
            the "Status" heading does not collapse row by row. */}
        <div className={cn(hasStatus ? "flex" : "hidden lg:flex", COL_STATUS)}>
          {reorder}
          {expiry}
        </div>

        {/* One tap adds the line to the delivery being unpacked. It used
            to open a per-item panel, which meant a twelve-line packing slip
            was twelve separate submissions.

            Unselected is the plain secondary treatment, not the accent.
            Ninety-seven outlined accent buttons out-shouted the six rows that
            actually need something done about them, which is the opposite of
            what colour is for on this screen. Selected keeps the accent fill —
            by then it is six or eight buttons, and they should stand out
            against the wall so the delivery is checkable at a glance. */}
        <div className={COL_ACTION}>
          <Button
            variant={inDelivery ? "default" : "outline"}
            onClick={() => onToggleDelivery(row)}
            aria-pressed={inDelivery}
            className="w-full"
          >
            {inDelivery ? "Added ✓" : "Book in"}
          </Button>
        </div>
      </div>

      {mode === "in" ? <BookIn row={row} onClose={onClose} /> : null}

      {mode === "edit" ? <EditItem row={row} onClose={onClose} /> : null}

      {mode === "detail" ? (
        <div className="mt-3 border-t pt-3">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5 sm:grid-cols-3 lg:grid-cols-5">
            <Field label="Vendor">{row.vendor || "—"}</Field>
            <Field label="Vendor item #">{row.vendorItemNumber || "—"}</Field>
            <Field label="Category">
              {row.topCategory} · {row.subcategory}
            </Field>
            <Field label="Pack size">{row.packSize || "—"}</Field>
            <Field label="Unit cost">
              {row.nexoraCostCents === null ? (
                <span className="text-muted-foreground">
                  no price on the sheet
                </span>
              ) : (
                <Money cents={row.nexoraCostCents} />
              )}
            </Field>
            <Field label="Value on shelf">
              {row.stockValueCents === null ? (
                "—"
              ) : (
                <Money cents={row.stockValueCents} />
              )}
            </Field>
            <Field label="Bin / location">{row.binLocation || "not set"}</Field>
            <Field label="Expiry">
              {row.expiresAt === null ? "not set" : shortDate(row.expiresAt)}
            </Field>
            <Field label="Last ordered">
              {row.lastOrderedAt === null
                ? "never"
                : `${shortDate(row.lastOrderedAt)} · ${relativeDay(row.lastOrderedAt)}`}
            </Field>
            <Field label="Count last moved">
              {row.updatedAt === null ? "never" : relativeDay(row.updatedAt)}
            </Field>
          </dl>

          <div className="mt-3 flex gap-2">
            <Button
              className="flex-1 lg:flex-none lg:px-6"
              onClick={() => onOpen(row.itemId, "in")}
            >
              Book in with expiry
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpen(row.itemId, "edit")}
            >
              Edit
            </Button>
          </div>
        </div>
      ) : null}
    </UiCard>
  );
});

/* -------------------------------------------------------------------------- */
/* Sections                                                                    */
/* -------------------------------------------------------------------------- */

type Section = {
  name: string;
  rows: Row[];
  needsOrdering: number;
  onOrder: number;
  expiring: number;
  /** Open on arrival. See `sectionsOf`. */
  openOnArrival: boolean;
};

/**
 * How many item rows are on screen before anything is tapped.
 *
 * A screen that arrives as twelve closed boxes is not a shelf, it is a filing
 * cabinet — and the warehouse's daily job is booking a delivery in, which needs
 * rows, not headers. So the top of the list arrives open and the tail arrives
 * folded: enough rows to work in immediately, few enough to scan.
 *
 * It is also the floor the click-count gate stands on. `tools/clickcount.mjs`
 * books six lines in without opening anything, so fewer than six "Book in"
 * buttons on arrival is a failed build, not a design preference.
 */
const ROWS_OPEN_ON_ARRIVAL = 12;

/**
 * The client's own sheet sections, in the order the server's "worst first" sort
 * hands them over.
 *
 * `Map` iterates in insertion order, so a section is placed by the first row of
 * it that appears — which is the most urgent row it contains. The section
 * holding the item that needs ordering most is therefore still first on screen,
 * and the sort inside each section is untouched.
 *
 * Every row appears in exactly one section. A pinned "needs ordering" group at
 * the top was the obvious alternative and it is worse: the same item would then
 * be on screen twice, with two "Book in" buttons that toggle each other, and a
 * category whose short item had been lifted out of it reads as complete when it
 * is not. "What do I need to reorder" is answered instead by the hero figure
 * above the list — which is also the filter — and by each section header saying
 * how many of its own items are short before it is ever opened.
 */
function sectionsOf(rows: Row[]): Section[] {
  const byName = new Map<string, Section>();
  for (const row of rows) {
    const name = row.subcategory || "Uncategorised";
    let section = byName.get(name);
    if (!section) {
      section = {
        name,
        rows: [],
        needsOrdering: 0,
        onOrder: 0,
        expiring: 0,
        openOnArrival: false,
      };
      byName.set(name, section);
    }
    section.rows.push(row);
    if (row.reorderState === "needs-ordering") section.needsOrdering += 1;
    if (row.reorderState === "on-order") section.onOrder += 1;
    if (row.expiryState === "expired" || row.expiryState === "soon") {
      section.expiring += 1;
    }
  }

  const sections = [...byName.values()];
  let shown = 0;
  for (const section of sections) {
    section.openOnArrival = shown < ROWS_OPEN_ON_ARRIVAL;
    shown += section.rows.length;
  }
  return sections;
}

/** One muted line under a section title. Never a pill: a header full of chips
 *  is the wall again, just louder. */
function sectionSummary(section: Section): string {
  const parts: string[] = [];
  if (section.needsOrdering > 0) parts.push(`${section.needsOrdering} need ordering`);
  if (section.onOrder > 0) parts.push(`${section.onOrder} on order`);
  if (section.expiring > 0) parts.push(`${section.expiring} expiring`);
  return parts.length > 0 ? parts.join(" · ") : "all at or above minimum";
}

/* -------------------------------------------------------------------------- */
/* The screen                                                                  */
/* -------------------------------------------------------------------------- */

type Filter = "all" | "low" | "expiring" | "unbinned";

type OpenCard = { id: string; mode: "detail" | "in" | "edit" };

/**
 * The search box, and the only thing on this screen that re-renders per
 * keystroke.
 *
 * The text has to echo immediately — a field that lags reads as a broken
 * keyboard. But it used to be the *screen's* state, so every letter re-rendered
 * ninety-seven `ItemCard`s to produce one changed character in an input, and on
 * the phone this screen is designed for that is where the typing lag came from.
 *
 * So the box owns its own text and hands the screen only the settled term, a
 * beat after the thumb stops. The parent re-renders once per search, not once
 * per letter.
 */
function SearchBox({ onSettle }: { onSettle: (term: string) => void }) {
  const [text, setText] = useState("");
  const settled = useDebouncedValue(text);

  useEffect(() => {
    onSettle(settled);
  }, [settled, onSettle]);

  return (
    <Input
      value={text}
      onChange={(event) => setText(event.target.value)}
      placeholder="Search by name, vendor, HS item # or bin"
      aria-label="Search warehouse inventory"
      className="lg:max-w-md"
    />
  );
}

function InventoryScreen() {
  /** The *settled* search term — `SearchBox` owns the letters. Everything on
   *  this screen that mentions the search quotes this, so the count and the
   *  term it is a count of are always the same search. */
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  /* Which card is open, and how — one fact, so it cannot be half-set. As two
     pieces of state the mode outlived the card it belonged to: closing a row
     left the old mode behind, and the next row opened in whatever panel the
     previous one had been showing. */
  const [open, setOpen] = useState<OpenCard | null>(null);
  /**
   * Which sections the warehouse has folded or unfolded by hand.
   *
   * Only the ones she touched. Everything else falls back to `openOnArrival`,
   * so the default can change — or the list can be re-sorted by the server —
   * without a stale map deciding what she sees. Keyed by section name because
   * that is what the header says; a section that vanishes from the payload
   * simply stops being looked up.
   */
  const [sectionOverrides, setSectionOverrides] = useState<
    Record<string, boolean>
  >({});
  const headerOffset = useHeaderOffset();

  /**
   * The delivery being unpacked, as one thing.
   *
   * A Henry Schein delivery is one box with one packing slip and a dozen lines
   * on it. Booking it in used to be a dozen separate submissions, each with its
   * own quantity stepper and its own retyped slip number — a rehearsal counted
   * roughly 36 taps against six cells in Excel, which by this project's own
   * standard is a blocking defect: if the app is slower than the spreadsheet
   * for a real job, the staff go back to the spreadsheet.
   *
   * The server already took an array of lines and a single reference. Only the
   * screen insisted on one at a time.
   *
   * Keyed by itemId so tapping the same row twice is a toggle rather than a
   * duplicate line — the server would sum duplicates, but she would have no way
   * to see she had added it twice.
   */
  const [delivery, setDelivery] = useState<Map<Row["itemId"], number>>(new Map());

  /* Every handler a row is given is a `useCallback` with no dependencies, and
     that is what makes `memo` on `ItemCard` worth anything: written inline, a
     fresh function per row per render defeats the comparison before it starts.
     They take the row, or its id, instead of closing over one. */
  const toggleDelivery = useCallback(
    (row: Row) =>
      setDelivery((current) => {
        const next = new Map(current);
        if (next.has(row.itemId)) next.delete(row.itemId);
        // Default to what it would take to clear the shortfall, because that is
        // the quantity she most often ordered. One when nothing is short.
        else next.set(row.itemId, Math.max(1, row.shortfall));
        return next;
      }),
    [],
  );

  const setDeliveryQty = useCallback(
    (itemId: Row["itemId"], qty: number) =>
      setDelivery((current) => {
        const next = new Map(current);
        if (qty <= 0) next.delete(itemId);
        else next.set(itemId, qty);
        return next;
      }),
    [],
  );

  const openCard = useCallback(
    (id: Row["itemId"], mode: OpenCard["mode"]) => setOpen({ id, mode }),
    [],
  );
  const closeCard = useCallback(() => setOpen(null), []);

  /**
   * Keep the last payload on screen while a new one is in flight.
   *
   * Without this, every keystroke in the search box and every filter chip
   * returns `undefined` for a beat and the whole list flashes back to a
   * skeleton — which at 390px looks exactly like the app breaking. The state
   * marker below stays "ready" for the same reason: the screen genuinely has
   * data. This screen had the trick open-coded first; it is `src/lib/useStable`
   * now because Requests and Approvals needed the identical three lines.
   */
  const data = useStable(useQuery(api.inventory.list, { search, filter }));

  /* Partitioned once per payload, not once per render.
   *
   * `sectionsOf` walks all ninety-seven rows and hands back brand-new `rows`
   * arrays, so re-running it every render gave every section a new array
   * identity and made memoising anything below it pointless — a row toggling,
   * a card opening, a section folding, the action bar re-measuring itself and
   * each settled search all reconciled the whole list. It only changes when the
   * server sends a different payload, so that is when it is recomputed. */
  const sections = useMemo(
    () => (data === undefined ? [] : sectionsOf(data.rows)),
    [data],
  );

  if (data === undefined) {
    return (
      <div data-screen-state="loading" className="space-y-4">
        <SectionHeading title="Warehouse" subtitle="Loading the shelf…" />
        <Loading rows={6} label="Loading warehouse inventory" />
      </div>
    );
  }

  /* A search result that is inside a folded section is a search that found
     nothing, as far as the person holding the phone is concerned. While a term
     is in the box every section is open and the fold control stands down. */
  const searching = search.trim().length > 0;
  const allOpen = sections.every(
    (section) => sectionOverrides[section.name] ?? section.openOnArrival,
  );

  /** Below minimum but already ordered from the vendor — the difference
   *  between the "Below min" chip and the red line under the title. */
  const onOrder = data.counts.low - data.counts.needsOrdering;

  /**
   * A chip that selects everything is not a filter, and one that can never
   * match anything is a dead end you can tap.
   *
   * Both were on screen: "No bin 97", because no bin location has been entered
   * against any item yet, and "Expiry 0", because nothing on the shelf carries
   * a date. So a chip is offered only when it would actually narrow the list —
   * or when it is the filter currently in force, so the way back out is never
   * the thing that disappears.
   */
  const filterChips = (
    [
      /* No "Below min" chip. It counted 7 while the lead card counted 6 — the
         same question with two answers a thumb-width apart, and a rehearsal
         stalled on exactly that. The two numbers are both true (below minimum
         includes the ones already on order) but only one of them is the job, so
         the card is now the only figure and the only way to that filter. The
         reconciliation lives in the card's own quiet subtext. */
      { value: "expiring", label: "Expiry", count: data.counts.expiring },
      { value: "unbinned", label: "No bin", count: data.counts.unbinned },
    ] as Array<{ value: Filter; label: string; count: number }>
  ).filter(
    (chip) =>
      filter === chip.value ||
      (chip.count > 0 && chip.count < data.counts.items),
  );

  return (
    /* No bottom clearance here. The <ActionBar> this screen parks measures
       itself and the shell's `<main>` pads by that height — so a screen no
       longer has to know a bar exists, or guess how tall it got. */
    <div data-screen-state="ready">
      <SectionHeading
        title="Warehouse"
        subtitle={`${data.counts.items} items · ${formatCentsCompact(
          data.totalValueCents,
        )} on the shelf${
          data.pricePendingCount > 0
            ? ` · ${data.pricePendingCount} with no price`
            : ""
        }`}
      />

      {/* The one question this screen exists to answer, at the size it deserves,
          and it is the filter as well as the answer — tapping it shows exactly
          the items it counts.

          It has to reconcile with the "Below min" chip beside it. They count
          different things on purpose: the chip is every item under its minimum,
          this is the subset nobody has ordered yet (see `reorderState` in
          convex/inventory.ts — an item a manager already ordered from the vendor
          is green, not red). Printed as two bare totals they read as the app
          contradicting itself, so the hint says both numbers and the gap. */}
      <button
        type="button"
        onClick={() => setFilter(filter === "low" ? "all" : "low")}
        aria-pressed={filter === "low"}
        className={cn(
          "mt-3 block w-full rounded-xl text-left transition-shadow sm:max-w-sm",
          filter === "low" ? "ring-2 ring-ring" : "hover:shadow-md",
        )}
      >
        <Tile
          size="hero"
          tone={data.counts.needsOrdering > 0 ? "red" : "green"}
          value={data.counts.needsOrdering}
          label={
            data.counts.needsOrdering === 1
              ? "item needs ordering"
              : "items need ordering"
          }
          hint={
            data.counts.low === 0
              ? "Every item is at or above its minimum."
              : filter === "low"
                ? onOrder > 0
                  ? `Showing all ${data.counts.low} below minimum, including the ${onOrder} ` +
                    "already ordered from the vendor. Tap to show the whole shelf."
                  : "Showing only these. Tap to show the whole shelf."
                : onOrder > 0
                  ? `${data.counts.low} are below minimum; the other ${onOrder} ${
                      onOrder === 1 ? "is" : "are"
                    } already ordered from the vendor. Tap to see them.`
                  : "Tap to see them."
          }
        />
      </button>

      {/* Sticky, because the alternative on a hundred-row list is scrolling
          back to the top to change one letter of a search. Offset by the real
          height of the app header rather than a guessed constant. */}
      <div
        style={{ top: headerOffset }}
        className="sticky z-20 -mx-4 mt-4 space-y-2 border-b bg-background/95 px-4 pt-2 pb-2.5 backdrop-blur lg:-mx-8 lg:px-8"
      >
        <SearchBox onSettle={setSearch} />

        {filterChips.length > 0 ? (
          <Chips<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: "all", label: "All", count: data.counts.items },
              ...filterChips,
            ]}
          />
        ) : null}

        {/* Say what the search did.
            A rehearsal typed "Henry Schein" — a real vendor, and one this box
            says it searches — got all 97 rows back, and concluded the search was
            broken. It was not: every item on the client's sheet is Henry Schein,
            so 97 of 97 is the right answer. A search that silently returns
            everything is indistinguishable from a search that does nothing, and
            the number is the whole difference. */}
        {search.trim().length > 0 ? (
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {data.matched === 0
              ? `Nothing matches "${search.trim()}"`
              : data.matched === data.counts.items
                ? `All ${data.matched} items match "${search.trim()}"`
                : `${data.matched} of ${data.counts.items} items match "${search.trim()}"`}
          </p>
        ) : null}
      </div>

      {data.rows.length === 0 ? (
        <div className="mt-4">
          <Empty
            title={search ? `Nothing matches “${search}”` : "Nothing here"}
            body={
              search
                ? "Try the vendor item number, or clear the search."
                : filter === "low"
                  ? "Every item is at or above its minimum."
                  : filter === "expiring"
                    ? "Nothing on the shelf is near its expiry date."
                    : filter === "unbinned"
                      ? "Every item has a bin location."
                      : "There are no active items in the catalogue."
            }
            tone={search || filter === "all" ? "neutral" : "good"}
          />
        </div>
      ) : (
        <>
          {/* Column headings, desktop only. The rows below use the same widths,
              so at 1280 this reads as a table without any of a table's problems
              at 390 — where the same four blocks simply wrap. */}
          <div className="mt-4 hidden items-center gap-x-3 pr-4 pl-5 text-xs font-medium text-muted-foreground lg:flex">
            <span className={COL_NAME}>Item</span>
            <span className={cn(COL_COUNT, "text-right")}>On hand</span>
            <span className={COL_STATUS}>Status</span>
            <span className={COL_ACTION} />
          </div>

          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {sections.length} {plural(sections.length, "section")} ·{" "}
              {data.rows.length} {plural(data.rows.length, "item")}
              {searching ? " · all open while searching" : ""}
            </p>
            <Button
              hidden={searching}
              variant="ghost"
              onClick={() =>
                setSectionOverrides(
                  Object.fromEntries(
                    sections.map((section) => [section.name, !allOpen]),
                  ),
                )
              }
              /* `hidden` survives the base's `inline-flex`, which is worth
                 saying out loud because it looks like it should not: Tailwind's
                 preflight spells the attribute `display: none !important`, so a
                 display utility cannot bring a hidden control back. The two
                 undone base classes are for the flex row this sits in —
                 `shrink-0` and `whitespace-nowrap` together would stop
                 "Collapse all" giving way to the count beside it at 390px,
                 which is the one direction this row is allowed to break. */
              className="-mr-2 shrink text-primary whitespace-normal"
            >
              {allOpen ? "Collapse all" : "Expand all"}
            </Button>
          </div>

          <div className="mt-2 space-y-2">
            {sections.map((section) => (
              <CollapsibleSection
                key={section.name}
                title={section.name}
                count={section.rows.length}
                summary={sectionSummary(section)}
                tone={
                  section.needsOrdering > 0
                    ? "red"
                    : section.onOrder > 0
                      ? "green"
                      : "gray"
                }
                /* Not sticky. The search-and-filter bar above is already
                   sticky and already costs ~110px of a 390px screen; a second
                   sticky strip under it either overlaps it or eats a third of
                   the viewport, and both are worse than a header that scrolls
                   away with its own rows. */
                sticky={false}
                open={
                  searching
                    ? true
                    : (sectionOverrides[section.name] ?? section.openOnArrival)
                }
                onOpenChange={(isOpen) =>
                  setSectionOverrides((current) => ({
                    ...current,
                    [section.name]: isOpen,
                  }))
                }
              >
                {/* A thunk, not elements. Passed as elements, every row in
                    every *closed* section was still constructed on each render
                    and then thrown away — the fold saved mounting ninety-seven
                    cards, not building them. Built here only when the section
                    is open, a folded section costs nothing at all. */}
                {() => (
                  <div className="space-y-1.5 p-1.5">
                    {section.rows.map((row) => (
                      <ItemCard
                        key={row.itemId}
                        row={row}
                        mode={open?.id === row.itemId ? open.mode : "closed"}
                        onOpen={openCard}
                        onClose={closeCard}
                        inDelivery={delivery.has(row.itemId)}
                        onToggleDelivery={toggleDelivery}
                      />
                    ))}
                  </div>
                )}
              </CollapsibleSection>
            ))}
          </div>
        </>
      )}

      {data.matched > data.rows.length ? (
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Showing {data.rows.length} of {data.matched} matching items. Search to
          narrow it down.
        </p>
      ) : null}

      <DeliveryBar
        rows={data.rows}
        delivery={delivery}
        onQty={setDeliveryQty}
        onClear={() => setDelivery(new Map())}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Error state                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Convex's `useQuery` **throws** on a server error — it never returns null. A
 * clinic role reaching this route is refused by the query, and that refusal has
 * to land as a readable card rather than as the root boundary blanking the
 * screen. Retry is a page reload: there is no attempt counter to bump here.
 */
export function Inventory() {
  return (
    <ScreenBoundary
      title="Inventory could not load"
      heading={<SectionHeading title="Warehouse" />}
      onError={(error, info) =>
        console.error("Inventory screen failed:", error, info.componentStack)
      }
    >
      <InventoryScreen />
    </ScreenBoundary>
  );
}
